import { Box, Card, CardContent, Typography, FormControl, Select, MenuItem, CircularProgress } from '@mui/material';
import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DoneIcon from '@mui/icons-material/Done';
import { ISearchDealerResult } from '@/types/ISearchDealerResult';
import { getStateAbbreviation } from '@/utils/staticData/getStateAbbreviation';
import './DealersList.css';

interface DealerListProps {
  dealers: ISearchDealerResult[];
  isLoading?: boolean;
  currentSearch?: string;
  currentCoordinates?: { latitude: number; longitude: number } | null;
  onDealerClick?: (dealerId: number) => void;
  onSortChange: (sortBy: string) => void;
}

const DealerList = ({ dealers = [], isLoading = false, onDealerClick, onSortChange }: DealerListProps) => {
  const [sortBy, setSortBy] = useState('distance');
  const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'distance', label: 'Distance' },
  ];

  const getRenderValue = (selected: string) =>
    sortOptions.find((opt) => opt.value === selected)?.label || 'Sort';

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box className='results-container'>
        <Typography className='results-text'>
          Results
        </Typography>
        <FormControl className='sort-dropdown' sx={{ width: 98 }}>
          <Select
            value={sortBy}
            displayEmpty
            IconComponent={ExpandMoreIcon}
            renderValue={getRenderValue}
            MenuProps={{
              PaperProps: {
                className: 'sort-dropdown-menu'
              }
            }}
            onChange={(e) => {
              const newSortBy = e.target.value;
              setSortBy(newSortBy);
              if (onSortChange) {
                onSortChange(newSortBy);
              }
            }}
          >
            <MenuItem
              disableTouchRipple
              sx={{
                display: "none",
              }}
              value=""
            />
            {sortOptions.map((opt) => (
              <MenuItem
                key={opt.value}
                disableTouchRipple
                value={opt.value}
              >
                {opt.label}
                {sortBy === opt.value ? (
                  <DoneIcon
                    color="primary"
                    sx={{
                      marginLeft: 5,
                    }}
                  />
                ) : null}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box className="dealer-list-container">
        {isLoading ? (
          <Box className="loading-container">
            <CircularProgress />
          </Box>
        ) : dealers.length > 0 ? (
          dealers.map((installer) => (
            <Card
              key={installer.id}
              className="dealer-list-item"
              onClick={() => onDealerClick?.(installer.id)}
              sx={{ cursor: onDealerClick ? 'pointer' : 'default' }}
            >
              <CardContent className="content-wrapper">
                <Box className="dealer-header">
                  <Typography className="name">
                    {installer.companyName}
                  </Typography>
                  <Typography className="distance">
                    {installer.distance?.toFixed(1)} mi
                  </Typography>
                </Box>
                <Typography className="street">
                  {installer.streetAddress}
                  <br />
                  {installer.city}, {getStateAbbreviation(installer.state)} {installer.zipCode}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                  <Box
                    className="directions"
                    component="span"
                    onClick={() => {
                      const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${installer.latitude},${installer.longitude}`;
                      window.open(directionsUrl, '_blank');
                    }}
                  >
                    Directions
                  </Box>
                  <Box sx={{ color: '#FF6B00', mx: 1, fontWeight: 700, fontFamily: 'Mulish, sans-serif' }}>|</Box>
                  <Box
                    className="call"
                    component="span"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`tel:${installer.phoneNumber}`, '_self');
                    }}
                  >
                    Call {installer.phoneNumber}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))
        ) : (
          <Box className="no-results-container">
            <Box>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 0, fontFamily: 'Mulish, sans-serif' }}>
                No installers found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '11px' }}>
                Try searching for a different location or check back later
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DealerList; 